import React, { useContext } from 'react'
import styled from '@emotion/styled'
import { Helmet } from 'react-helmet'
import { makeImgResizerUrl } from '@jelly/utils'
import { array, bool, object, string } from 'prop-types'
import Media from 'react-media'
import { JsonLd } from 'react-schemaorg'

// Import Components
import { ArticleBody } from './ArticleBody'
import {
	AdsenseContext,
	Advertisement,
	ArticleSchema,
	ContentToolBar,
	DfpAdUnitSetup,
	Layout,
	SiteConfigContext,
} from '@jelly/components'

// Styled Components via Emotion
const ArticleContainer = styled.div`
	height: 100%;
	width: 100%;
`

Article.propTypes = {
	author: object.isRequired,
	bestPicks: array,
	byLine: string.isRequired,
	breadcrumbs: object.isRequired,
	canonical: string.isRequired,
	channels: object.isRequired,
	content: string.isRequired,
	docType: string.isRequired,
	guid: string.isRequired,
	hasVwo: bool,
	lastModified: string.isRequired,
	meta: object.isRequired,
	parsedContent: object.isRequired,
	published: string,
	relatedArticles: array,
	relatedExperts: array,
	relatedReviews: array,
	relatedSponsoredArticles: array,
	resource: object,
	slug: string,
	sponsorship: object,
	structuredData: string,
	subtitle: string.isRequired,
	thumbnail: object.isRequired,
	title: string.isRequired,
	tocElements: array,
	type: string,
}

/**
 * This is the Article Template Wrapper
 * Please check here for Article page variation requirements: https://bizdotcom.atlassian.net/wiki/spaces/BCS/pages/358252551/BDC+Article+Page+Variations
 */
export function Article(props) {
	const {
		author,
		bestPicks,
		byLine,
		breadcrumbs,
		canonical,
		channels,
		content,
		docType,
		guid,
		lastModified,
		meta,
		parsedContent,
		published,
		relatedArticles = [],
		relatedExperts,
		relatedReviews,
		relatedSponsoredArticles,
		resource,
		slug,
		sponsorship = { sponsorType: null },
		structuredData,
		subtitle,
		template,
		thumbnail = {},
		title,
		tocElements,
		type,
	} = props
	const {
		siteConstants: { ALL_SPONSOR_TYPES, LONG_FORM },
	} = useContext(SiteConfigContext)
	const resizedImage = makeImgResizerUrl({ src: thumbnail.path, method: 'w', w: '700' })
	const isSponsoredArticle = sponsorship && ALL_SPONSOR_TYPES.includes(sponsorship.sponsorType)

	const { adsenseMediaQuery, hasAdsense } = useContext(AdsenseContext)

	// temporary: split related articles into two related sections for right rail and related end tile cards
	const relatedRightRailArticles = relatedArticles && relatedArticles.slice(0, 7)
	let relatedEndArticles = []
	if (template === LONG_FORM) {
		relatedEndArticles = relatedArticles
	} else {
		relatedEndArticles = relatedArticles.length > 7 ? relatedArticles.slice(7) : null
	}
	return (
		<ArticleContainer>
			<Layout inverseLinkColors={true} meta={meta} hasAdDisclosure={true}>
				{/* override meta data for social sharing, should be the thumbnail of the article */}
				<Helmet
					meta={[
						{ name: 'twitter:image', content: resizedImage },
						{ property: 'og:image', content: thumbnail.path },
					]}
				/>
				<ContentToolBar breadcrumbs={breadcrumbs} canonical={canonical} title={title} />
				<Advertisement />
				<JsonLd
					item={ArticleSchema({ author, canonical, content, lastModified, published, title, thumbnail })}
				/>
				{structuredData && <JsonLd item={structuredData} />}
				<ArticleBody
					author={author}
					byLine={byLine}
					canonical={canonical}
					channels={channels}
					bestPicks={bestPicks}
					docType={docType}
					heroCaption={thumbnail.caption}
					mainContent={content}
					lastModified={lastModified}
					parsedContent={parsedContent}
					published={published}
					resource={resource}
					relatedArticles={relatedRightRailArticles}
					relatedExperts={relatedExperts}
					relatedReviews={relatedReviews}
					relatedEndArticles={relatedEndArticles}
					relatedSponsoredArticles={relatedSponsoredArticles}
					sponsorship={sponsorship}
					subtitle={subtitle}
					title={title}
					template={template}
					thumbnail={thumbnail}
					tocElements={tocElements}
					type={type}
					slug={slug}
				/>
				{hasAdsense && (
					<Media query={adsenseMediaQuery} defaultMatches={false}>
						{(matches) =>
							(matches || isSponsoredArticle) && (
								<DfpAdUnitSetup channels={channels} docType={docType} guid={guid} />
							)
						}
					</Media>
				)}
			</Layout>
		</ArticleContainer>
	)
}
